import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", contact: "", email: "", phone: "", address: "" };

export default function Suppliers() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/suppliers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditId(s.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success("Supplier saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Suppliers</h1>
          <p className="text-xs text-zinc-600 mt-1">Vendor master · components source for GRNs</p>
        </div>
        {canEdit && (
          <button onClick={openCreate} data-testid="add-supplier-btn" className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2">
            <Plus size={14} weight="bold" /> Add Supplier
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="suppliers-table">
          <thead>
            <tr>
              <th>Code</th><th>Name</th><th>Contact</th><th>Email</th><th>Phone</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-zinc-500 py-8">No suppliers yet.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.code}`}>
                <td className="font-bold">{s.code}</td>
                <td>{s.name}</td>
                <td>{s.contact}</td>
                <td>{s.email}</td>
                <td>{s.phone}</td>
                <td className="text-right">
                  {canEdit && (
                    <button onClick={() => openEdit(s)} className="brut-tag brut-tag-white mr-1" data-testid={`edit-supplier-${s.code}`}>
                      <PencilSimple size={10} weight="bold" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(s.id)} className="brut-tag brut-tag-red" data-testid={`delete-supplier-${s.code}`}>
                      <Trash size={10} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title={editId ? "Edit Supplier" : "New Supplier"} onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-3" data-testid="supplier-form">
            <Field label="Code" required value={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="supplier-code" />
            <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="supplier-name" />
            <Field label="Contact Person" value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} testid="supplier-contact" />
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="supplier-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="supplier-phone" />
            <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} textarea testid="supplier-address" />
            <button type="submit" className="brut-btn brut-btn-primary px-4 py-2 mt-2" data-testid="save-supplier-btn">
              Save Supplier
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative brut-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b-2 border-[#334155] px-5 py-3 bg-[#1e293b] text-white">
          <h3 className="font-display uppercase tracking-tight text-lg">{title}</h3>
          <button onClick={onClose} className="text-white" data-testid="modal-close"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, required, type = "text", textarea, testid, ...rest }) {
  return (
    <div>
      <label className="brut-label">{label}{required && " *"}</label>
      {textarea ? (
        <textarea required={required} value={value} onChange={(e) => onChange(e.target.value)} className="brut-input mt-1" rows={3} data-testid={testid} {...rest} />
      ) : (
        <input required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="brut-input mt-1" data-testid={testid} {...rest} />
      )}
    </div>
  );
}
